/**
 * One pass over the scene, shared by every rule that judges the composition across time.
 *
 * Posing is the expensive part and it is not getting cheaper: against a sandboxed luminary each pose
 * is a round trip to the composition frame, a serialization of the whole document, and a fresh parse
 * and layout of the copy that comes back — so a rule taking its own pass costs as much as every
 * other rule put together. The poses are taken once, memoized on the context, and each one is
 * measured for everything any rule needs while the document is still the posed one.
 *
 * Measuring inside the loop is not an optimization but a requirement: there is a single copy, so
 * posing it detaches the document the previous pose handed out. Nothing here may keep a `Document`
 * or an `Element` past the iteration that produced it.
 */
import type { RuntimeLintContext } from './runtimeRules';
import { poserFor } from './posing';
import type { Pose } from './poseSignature';
import { capturePose } from './poseSignature';
import {
    collectTextLeaves,
    isHiddenOnlyByVisibility,
    isLegible,
    lineOf,
    textOf,
} from './textLeaves';

/**
 * Sampling cadence: three samples per second of playback, so the grid resolves the same window on a
 * short scene as on a long one, bounded either way — enough to say anything about a very short
 * scene, capped so a long one doesn't pay for hundreds of poses.
 */
const TARGET_SAMPLE_SPACING_MS = 333;
const MIN_SAMPLE_COUNT = 20;
const MAX_SAMPLE_COUNT = 200;

export function sampleCount(sceneDurationMs: number | undefined): number {
    if (
        typeof sceneDurationMs !== 'number' ||
        !Number.isFinite(sceneDurationMs) ||
        sceneDurationMs <= 0
    ) {
        return MIN_SAMPLE_COUNT;
    }
    const spaced = Math.ceil(sceneDurationMs / TARGET_SAMPLE_SPACING_MS) + 1;
    return Math.min(MAX_SAMPLE_COUNT, Math.max(MIN_SAMPLE_COUNT, spaced));
}

/**
 * The elements that are the graphic's content rather than its backdrop: every labeled asset, what
 * is inside one, and the wrappers that carry one. An atmosphere field drifting behind a settled frame
 * leaves nothing unresolved for the viewer, so it has no bearing on whether the graphic has settled.
 * A composition with no labels can't be split this way, and all of it counts.
 */
function contentFilter(document: Document): ((element: Element) => boolean) | undefined {
    if (!document.querySelector('[data-codeo-asset]')) {
        return undefined;
    }
    return (element) =>
        element.closest('[data-codeo-asset]') !== null ||
        element.querySelector('[data-codeo-asset]') !== null;
}

export interface PoseSample {
    /** Where in the scene this pose was taken, 0..1. */
    progress: number;
    /** The frame as a comparable value, for the rules asking whether anything changed. */
    pose: Pose;
    /** The same, narrowed to the graphic's content — see `contentFilter`. */
    contentPose: Pose;
    /**
     * Whether each element rendering its own copy was legible here, keyed by that element's index
     * within the pose. A leaf absent from this map rendered no copy of its own at this frame, which
     * is what a reader would call "not on screen".
     */
    legibleByIndex: Map<number, boolean>;
    /** The leaves only `visibility` kept from painting here, at full opacity, by the same index. */
    hiddenByIndex: Set<number>;
}

export interface ScenePass {
    poses: PoseSample[];
    /**
     * What each leaf and the line it sits in say at the *last* pose, keyed by element index. A
     * counter or a typewriter rewrites its text as it plays, so what it finally says is the copy the
     * viewer is meant to read — and it can only be read off the posed document, which is gone by the
     * time a rule runs.
     */
    settledByIndex: Map<number, { text: string; line: string }>;
}

// The runtime rules run in sequence against one loaded composition, and the asset builds a fresh
// context per lint, so the pass cannot go stale.
const passByContext = new WeakMap<RuntimeLintContext, Promise<ScenePass | null>>();

/**
 * The scene sampled evenly from start to end. Null when the composition cannot be posed at all,
 * which is the signal for a rule to report nothing rather than measure the same frame repeatedly and
 * read the stillness as a finding.
 *
 * Call this only once a rule's own gate has passed: the first caller pays for the pass, so a rule
 * that would have discarded the result must not be what triggers it.
 */
export function samplePoses(context: RuntimeLintContext): Promise<ScenePass | null> {
    const cached = passByContext.get(context);
    if (cached) {
        return cached;
    }
    const pass = takePoses(context);
    passByContext.set(context, pass);
    return pass;
}

async function takePoses(context: RuntimeLintContext): Promise<ScenePass | null> {
    const { timeline, canvasSize } = context;
    const poseAt = poserFor(context);
    if (!timeline || !poseAt || typeof timeline.duration !== 'function') {
        return null;
    }
    const count = sampleCount(context.sceneDurationMs);
    const poses: PoseSample[] = [];
    const settledByIndex = new Map<number, { text: string; line: string }>();
    // Read off the first pose and reused: which elements are content is a fact about the markup, and
    // re-deriving it per pose would let a label arriving mid-scene change what the poses mean to
    // compare. The predicate itself walks from the element, so it holds for any posed document.
    let isContent: ((element: Element) => boolean) | undefined;
    for (let index = 0; index < count; index++) {
        const progress = index / (count - 1);
        const posed = await poseAt(progress);
        if (index === 0) {
            isContent = contentFilter(posed);
        }
        const legibleByIndex = new Map<number, boolean>();
        const hiddenByIndex = new Set<number>();
        for (const leaf of collectTextLeaves(posed)) {
            legibleByIndex.set(leaf.index, isLegible(leaf, canvasSize));
            if (isHiddenOnlyByVisibility(leaf)) {
                hiddenByIndex.add(leaf.index);
            }
            if (index === count - 1) {
                settledByIndex.set(leaf.index, {
                    text: leaf.preview,
                    line: textOf(lineOf(leaf.element)) || leaf.preview,
                });
            }
        }
        const pose = capturePose(posed);
        poses.push({
            progress,
            pose,
            contentPose: isContent ? capturePose(posed, isContent) : pose,
            legibleByIndex,
            hiddenByIndex,
        });
    }
    return { poses, settledByIndex };
}
